/** @format */

/* eslint require-await: "warn" */
import { TFunction } from 'i18next';
import { SingletonRouter } from 'next/router';

import { decimalToCent } from '@toucan/helpers/currency';
import { productConditionStringToEnum } from '@toucan/helpers/products';

import {
    BasketProduct,
    BasketProductState,
    MPBXBasketProduct,
} from '@toucan/client/core/basket';
import {
    BasketTransactionSummary,
    MixedTransaction,
} from '@toucan/client/global/transaction';

/**
 * Redirect the user to the model page
 *
 * @param marketSlug the active market slug
 * @param modelSlug the model to link to
 * @param router the next page  router
 */
export async function findAnotherProduct(
    marketSlug: string,
    modelSlug: string,
    t: TFunction,
    router: SingletonRouter
): Promise<boolean> {
    const productSlug = t('product', { ns: 'urls' });
    const pathname = `/model?slug=${modelSlug}`;
    const url = `/${marketSlug}/${productSlug}/${modelSlug}`;

    return router.push(
        {
            pathname,
        },
        url
    );
}

/**
 * Redirect the user to the checkout
 *
 * @param marketSlug the active market slug
 * @param isLoggedIn whether the user is logged in
 * @param t the translation function
 * @param router the next page  router
 */
export function navigateToCheckout(
    marketSlug: string,
    isLoggedIn: boolean,
    t: TFunction,
    router: SingletonRouter
): Promise<boolean> {
    const pathname = isLoggedIn ? '/checkout' : '/checkout-login';
    const url = isLoggedIn
        ? `/${marketSlug}/s/${t('checkout', { ns: 'urls' })}`
        : `/${marketSlug}/s/${t('checkout', {
              ns: 'urls',
          })}/${t('login', { ns: 'urls' })}`;

    return router.push(
        {
            pathname,
        },
        url
    );
}

export function hasBasketChanged(
    { itemsToBuy: originalItemsToBuy }: MixedTransaction,
    { itemsToBuy: latestItemsToBuy }: BasketTransactionSummary
) {
    if (originalItemsToBuy.length !== latestItemsToBuy.length) {
        return true;
    }

    return originalItemsToBuy.some((originalItem) => {
        const latestVersionOfItem = latestItemsToBuy.find(
            (latestItem) => latestItem.productId === originalItem.productId
        );

        return latestVersionOfItem?.value !== originalItem.value;
    });
}

export function transformLegacyProductDataForBasket(
    product: MPBXBasketProduct
): BasketProduct {
    return {
        ...product,
        value: decimalToCent(parseFloat(product.listPrice)),
        condition: productConditionStringToEnum(product.condition),
        name: product.name ?? product.modelName,
        isSold: product.state === BasketProductState.SHIPPED,
    };
}
